import React, { useEffect, useState } from "react";
import useFetch from "../../Shared/useFetch";
import { PflegeCategory } from "../../../shared/types";
import { Box, Button, LinearProgress, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import CustomSnackbar, {
  CustomSnackbarProps,
} from "../../Shared/CustomSnackbar";

function AdminPflege() {
  const FETCH = useFetch("/");
  const [categories, setCategories] = useState<PflegeCategory[]>([]);
  const [names, setNames] = useState<Record<number, string>>({});
  const [pdfs, setPdfs] = useState<Record<number, File | null>>({});
  const [newName, setNewName] = useState("");
  const [newPdf, setNewPdf] = useState<File | null>(null);
  // Bumping the key remounts the create form to clear the file input.
  const [createFormKey, setCreateFormKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [actionSuccess, setActionSuccess] = useState(false);
  const [failMessage, setFailMessage] = useState(
    "Die Aktion ist fehlgeschlagen.",
  );

  const loadCategories = () => {
    FETCH.getPflegeCategories()
      .then((data) => {
        setCategories(data);
        setNames(
          Object.fromEntries(
            data.map((category) => [category.id, category.name]),
          ),
        );
        setPdfs({});
      })
      .catch((error) => {
        console.error(error);
      });
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const extractFailMessage = async (error: any) => {
    if (error instanceof Response) {
      try {
        const body = await error.json();
        if (body?.message) {
          return body.message;
        }
      } catch {
        // fall through to default message
      }
    }
    return "Die Aktion ist fehlgeschlagen.";
  };

  const runAction = (action: Promise<unknown>) => {
    setLoading(true);
    setActionSuccess(false);

    action
      .then(() => {
        setActionSuccess(true);
        setNewName("");
        setNewPdf(null);
        setCreateFormKey((key) => key + 1);
        loadCategories();
      })
      .catch(async (error) => {
        console.error(error);
        setFailMessage(await extractFailMessage(error));
      })
      .finally(() => {
        setLoading(false);
        setOpenSnackbar(true);
      });
  };

  const handleCreate = (event: React.FormEvent) => {
    event.preventDefault();

    const formContent = new FormData();
    formContent.append("name", newName);
    if (newPdf) {
      formContent.append("pdf", newPdf as Blob);
    }

    runAction(FETCH.createPflegeCategory(formContent));
  };

  const handleUpdate = (id: number) => {
    const formContent = new FormData();
    formContent.append("name", names[id] ?? "");
    const pdf = pdfs[id];
    if (pdf) {
      formContent.append("pdf", pdf as Blob);
    }

    runAction(FETCH.updatePflegeCategory(id, formContent));
  };

  const handleDelete = (id: number) => {
    runAction(FETCH.deletePflegeCategory(id));
  };

  const handleSnackbarClose = (
    _event: React.SyntheticEvent | Event,
    _reason?: string,
  ): void => {
    setOpenSnackbar(false);
  };

  const snackbarProps: CustomSnackbarProps = {
    actionSuccess: actionSuccess,
    openSnackbar,
    handleSnackbarClose,
    successMessage: "Die Änderung wurde erfolgreich gespeichert.",
    failMessage,
  };

  return (
    <div className="container px-4 mx-auto flex-row">
      <h1 className="text-center text-5xl text-gray-700 my-4">Pflege</h1>
      {loading && (
        <Box sx={{ width: "100%" }}>
          <LinearProgress />
        </Box>
      )}
      {!loading && (
        <div className="mx-auto max-w-2xl">
          <h2 className="text-2xl text-gray-700 my-4">
            Neue Kategorie anlegen
          </h2>
          <form
            key={createFormKey}
            onSubmit={handleCreate}
            className="flex flex-col gap-4 mb-8"
          >
            <TextField
              label="Name"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              size="small"
            />
            <input
              type="file"
              accept="application/pdf"
              onChange={(event) => setNewPdf(event.target.files?.[0] ?? null)}
            />
            <Button
              type="submit"
              variant="contained"
              endIcon={<AddIcon />}
              disabled={!newName.trim() || !newPdf}
            >
              Hinzufügen
            </Button>
          </form>

          <h2 className="text-2xl text-gray-700 my-4">Kategorien</h2>
          {categories.length === 0 && (
            <p className="text-gray-500">Noch keine Kategorien vorhanden.</p>
          )}
          <ul>
            {categories.map((category) => (
              <li
                key={category.id}
                className="flex flex-col gap-2 py-4 border-b border-gray-200"
              >
                <TextField
                  label="Name"
                  value={names[category.id] ?? ""}
                  onChange={(event) =>
                    setNames({ ...names, [category.id]: event.target.value })
                  }
                  size="small"
                />
                <a
                  href={category.pdfPath}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-petrol-400 underline"
                >
                  Aktuelle PDF ansehen
                </a>
                <label className="text-gray-500">
                  PDF ersetzen (optional):
                  <input
                    type="file"
                    accept="application/pdf"
                    className="block"
                    onChange={(event) =>
                      setPdfs({
                        ...pdfs,
                        [category.id]: event.target.files?.[0] ?? null,
                      })
                    }
                  />
                </label>
                <div className="flex gap-2">
                  <Button
                    variant="contained"
                    endIcon={<SaveIcon />}
                    onClick={() => handleUpdate(category.id)}
                    disabled={!(names[category.id] ?? "").trim()}
                  >
                    Speichern
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    endIcon={<DeleteIcon />}
                    onClick={() => handleDelete(category.id)}
                  >
                    Löschen
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      <CustomSnackbar {...snackbarProps} />
    </div>
  );
}

export default AdminPflege;
